import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Button } from './ui/Button';
import { Rocket, Users, Brain, Settings, CheckCircle2, FileBarChart, RefreshCw } from 'lucide-react';
interface SlideGoLiveProps {
  onContinue: () => void;
}
interface LoopStep {
  icon: React.ReactNode;
  label: string;
  items: string[];
  micro?: string;
}
export function SlideGoLive({
  onContinue
}: SlideGoLiveProps) {
  const [activeStep, setActiveStep] = useState(-1);
  const [showCenter, setShowCenter] = useState(false);
  const [showInsight, setShowInsight] = useState(false);
  const loopSteps: LoopStep[] = [{
    icon: <Rocket className="w-5 h-5" data-id="element-233" />,
    label: 'Go Live',
    items: ['Campaign runs', 'Across all channels']
  }, {
    icon: <Users className="w-5 h-5" data-id="element-234" />,
    label: 'Engage',
    items: ['HCPs interact', 'With Snaks']
  }, {
    icon: <Brain className="w-5 h-5" data-id="element-235" />,
    label: 'Learn',
    items: ['Behavioral data', 'Motivational insights', 'Engagement patterns'],
    micro: 'Not just what — but why'
  }, {
    icon: <Settings className="w-5 h-5" data-id="element-236" />,
    label: 'Optimize',
    items: ['Adjust messaging', 'Improve flow', 'Refine CTAs'],
    micro: 'Driven by actionable data'
  }];
  useEffect(() => {
    const timers = loopSteps.map((_, idx) => setTimeout(() => setActiveStep(idx), 600 + idx * 800));
    const centerTimer = setTimeout(() => setShowCenter(true), 600 + loopSteps.length * 800 + 300);
    const insightTimer = setTimeout(() => setShowInsight(true), 600 + loopSteps.length * 800 + 800);
    return () => {
      timers.forEach(clearTimeout);
      clearTimeout(centerTimer);
      clearTimeout(insightTimer);
    };
  }, []);
  // Positions for circular layout
  const positions = [{
    top: '5%',
    left: '50%',
    transform: 'translateX(-50%)'
  }, {
    top: '50%',
    right: '0%',
    transform: 'translateY(-50%)'
  }, {
    bottom: '5%',
    left: '50%',
    transform: 'translateX(-50%)'
  }, {
    top: '50%',
    left: '0%',
    transform: 'translateY(-50%)'
  } // left
  ];
  return <div className="h-full w-full flex flex-col px-8 py-10 md:px-14 md:py-12 lg:px-20 lg:py-14 max-w-7xl mx-auto" data-id="element-237">
      <motion.h2 initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} className="text-3xl md:text-4xl lg:text-5xl font-serif leading-tight text-white mb-6 text-center" data-id="element-238">
        Where the value <span className="text-teal-400" data-id="element-239">compounds</span>
      </motion.h2>

      <div className="flex-1 grid grid-cols-1 lg:grid-cols-12 gap-6 items-center" data-id="element-240">
        {/* Loop Visualization */}
        <div className="lg:col-span-7" data-id="element-241">
          {/* 2x2 Grid Loop */}
          <div className="grid grid-cols-2 gap-4 relative" data-id="element-242">
            {/* Circular arrows connecting the steps */}
            <svg className="absolute inset-0 w-full h-full pointer-events-none z-0" viewBox="0 0 400 320" data-id="element-243">
              <motion.path d="M 200 30 L 350 30 Q 380 30 380 60 L 380 130 Q 380 160 350 160 L 200 160" fill="none" stroke="#00D4AA" strokeWidth="2" strokeOpacity="0.3" strokeDasharray="6 4" initial={{
              pathLength: 0
            }} animate={{
              pathLength: activeStep >= 1 ? 1 : 0
            }} transition={{
              duration: 0.8
            }} data-id="element-244" />
              <motion.path d="M 200 160 L 200 190 Q 200 200 190 200 L 50 200 Q 20 200 20 220 L 20 260 Q 20 290 50 290 L 200 290" fill="none" stroke="#00D4AA" strokeWidth="2" strokeOpacity="0.3" strokeDasharray="6 4" initial={{
              pathLength: 0
            }} animate={{
              pathLength: activeStep >= 2 ? 1 : 0
            }} transition={{
              duration: 0.8
            }} data-id="element-245" />
              <motion.path d="M 200 290 L 200 30" fill="none" stroke="#00D4AA" strokeWidth="2" strokeOpacity="0.4" strokeDasharray="6 4" initial={{
              pathLength: 0
            }} animate={{
              pathLength: activeStep >= 3 ? 1 : 0
            }} transition={{
              duration: 1,
              delay: 0.3
            }} data-id="element-246" />
            </svg>

            {loopSteps.map((step, idx) => {
            const isActive = idx <= activeStep;
            return <motion.div key={idx} initial={{
              opacity: 0,
              scale: 0.9
            }} animate={{
              opacity: isActive ? 1 : 0.2,
              scale: isActive ? 1 : 0.9
            }} transition={{
              duration: 0.5
            }} className={`relative z-10 rounded-xl p-4 ${isActive ? 'bg-navy-700/60 border border-navy-500' : 'bg-navy-800/30 border border-navy-700/30'}`} data-id="element-247">
                  <div className="flex items-center gap-2.5 mb-3" data-id="element-248">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${isActive ? 'bg-teal-400 text-navy-900' : 'bg-navy-600 text-white/30'}`} data-id="element-249">
                      {step.icon}
                    </div>
                    <h4 className={`font-semibold text-sm ${isActive ? 'text-teal-400' : 'text-white/30'}`} data-id="element-250">
                      {step.label}
                    </h4>
                  </div>

                  <div className="space-y-1" data-id="element-251">
                    {step.items.map((item, i) => <div key={i} className={`text-xs flex items-center gap-1.5 ${isActive ? 'text-white/80' : 'text-white/20'}`} data-id="element-252">
                        <div className={`w-1 h-1 rounded-full flex-shrink-0 ${isActive ? 'bg-teal-400' : 'bg-navy-600'}`} data-id="element-253" />
                        {item}
                      </div>)}
                  </div>

                  {step.micro && <p className={`text-xs italic mt-2 pt-2 border-t ${isActive ? 'text-teal-400/70 border-navy-500' : 'text-white/15 border-navy-700'}`} data-id="element-254">
                      {step.micro}
                    </p>}
                </motion.div>;
          })}
          </div>

          {/* Center hero statement + reporting */}
          <motion.div initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: showCenter ? 1 : 0,
          y: showCenter ? 0 : 10
        }} transition={{
          duration: 0.6
        }} className="mt-5 flex items-center gap-4" data-id="element-255">
            <div className="flex-1 bg-navy-700/40 border border-teal-400/20 rounded-xl px-5 py-3" data-id="element-256">
              <p className="text-sm text-teal-400 font-medium" data-id="element-257">
                This is where campaigns become self-improving systems
              </p>
            </div>
            <div className="flex items-center gap-2 bg-navy-700/40 border border-navy-600 rounded-xl px-4 py-3" data-id="element-258">
              <FileBarChart className="w-4 h-4 text-teal-400/70" data-id="element-259" />
              <div className="text-xs text-white/60" data-id="element-260">
                <span className="text-white/80 font-medium block" data-id="element-261">
                  Built-in reporting
                </span>
                Automated · Recommendations · Tracking
              </div>
            </div>
          </motion.div>
        </div>

        {/* Insight Box */}
        <div className="lg:col-span-5" data-id="element-262">
          <motion.div initial={{
          opacity: 0,
          x: 20
        }} animate={{
          opacity: showInsight ? 1 : 0,
          x: showInsight ? 0 : 20
        }} transition={{
          duration: 0.6
        }} className="bg-navy-700/50 border border-navy-600 p-6 rounded-2xl" data-id="element-263">
            <div className="text-teal-400 font-medium mb-4 flex items-center gap-2 text-sm" data-id="element-264">
              <CheckCircle2 className="w-4 h-4" data-id="element-265" />
              Insight
            </div>
            <div className="text-lg leading-relaxed text-white/90 space-y-3" data-id="element-266">
              <p className="font-medium" data-id="element-267">
                This is not post-campaign reporting.
              </p>
              <p className="text-white/70" data-id="element-268">
                <span className="text-teal-400 mr-1" data-id="element-269">→</span>
                This is live optimization
              </p>
              <p className="text-white/70" data-id="element-270">
                <span className="text-teal-400 mr-1" data-id="element-271">→</span>
                While the campaign is running
              </p>
            </div>

            {/* Loop indicator */}
            <div className="mt-4 flex items-center gap-2 text-teal-400/50" data-id="element-272">
              <motion.div animate={{
              rotate: 360
            }} transition={{
              repeat: Infinity,
              duration: 5,
              ease: 'linear'
            }} data-id="element-273">
                <RefreshCw className="w-4 h-4" data-id="element-274" />
              </motion.div>
              <span className="text-xs uppercase tracking-widest font-medium" data-id="element-275">
                Continuous loop
              </span>
            </div>

            <div className="mt-5" data-id="element-276">
              <Button onClick={onContinue} data-id="element-277">Continue</Button>
            </div>
          </motion.div>
        </div>
      </div>
    </div>;
}